import { z } from 'zod';
import {
  PasswordProtectedError,
  TooManyRequestsError
} from '@/services/errors/studio-errors';

import { STUDIO_API_URL } from './constants';

type HTTPMethodeType = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';

/**
 * HTTP responses are untyped JSON at the network boundary. Callers are
 * expected to validate the response shape with Zod (typically inside MobX
 * stores) or accept the typed contract at their boundary. The
 * `unsafeJsonCast` helper below isolates the unavoidable boundary cast in
 * a single, documented location so the rest of the file stays free of
 * direct `as` assertions.
 */

type NestError = {
  statusCode: number;
  message: string | string[];
  error: string;
};

const nestErrorSchema = z.object({
  statusCode: z.number(),
  message: z.union([z.string(), z.array(z.string())]),
  error: z.string()
}) satisfies z.ZodType<NestError>;

export type ApiResponseEmpty = {
  statusCode: number;
};

/**
 * Boundary cast helper. JSON parsed from the network is `unknown`; this
 * helper performs the single documented cast to the caller's expected
 * `ResponseType`. Prefer validating with Zod in the caller when possible.
 */
const unsafeJsonCast = <T>(value: unknown): T => value as T;

export class HTTPWrapper<T> {
  private callable_url: string;

  constructor(storeURL: string) {
    this.callable_url = `${STUDIO_API_URL}/${storeURL}`;
  }

  private async _fetch<ResponseType, BodyType = unknown>(
    method: HTTPMethodeType,
    url: string,
    body?: BodyType,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<ResponseType>> {
    const finalURL = `${this.callable_url}${url}`;

    // Skip default Content-Type for bodyless GET/DELETE — some proxies and
    // CDNs reject Content-Type on bodyless requests. Caller-provided headers
    // always win (their values are spread last).
    const isBodyless =
      body === undefined && (method === 'GET' || method === 'DELETE');
    const defaultHeaders: Record<string, string> = isBodyless
      ? {}
      : { 'Content-Type': 'application/json' };

    const res = await fetch(finalURL, {
      method,
      headers: {
        ...defaultHeaders,
        ...opt?.headers
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined
    });

    if (res.status === 423) {
      throw new PasswordProtectedError();
    }

    if (res.status === 429) {
      throw new TooManyRequestsError();
    }

    let json_data: unknown;
    let parseFailed = false;
    let rawBody = '';

    try {
      json_data = await res.json();
    } catch (_error) {
      parseFailed = true;
      try {
        rawBody = await res.clone().text();
      } catch (_textError) {
        rawBody = '';
      }
    }

    if (!res.ok) {
      if (parseFailed) {
        const truncated = rawBody.slice(0, 500);
        throw new Error(`HTTP ${res.status} non-JSON response: ${truncated}`);
      }
      throw new Error(JSON.stringify(json_data));
    }

    // Successful responses with no body (empty 200, 204, etc.) are valid --
    // many backend endpoints return void on mutations.
    if (parseFailed) {
      return;
    }

    return unsafeJsonCast<ResponseType>(json_data);
  }

  private async _fetchWithForbidden<ResponseType>(
    url: string
  ): Promise<Maybe<ResponseType>> {
    try {
      const res = await fetch(`${this.callable_url}${url}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: undefined
      });

      const json_data: unknown = await res.json();

      if (!res.ok) {
        const parsedError = nestErrorSchema.safeParse(json_data);
        if (parsedError.success && parsedError.data.message) {
          const { message } = parsedError.data;
          if (Array.isArray(message)) {
            throw new Error(message[0]);
          }
          throw new Error(message);
        }
        throw new Error(await res.text());
      }

      return unsafeJsonCast<ResponseType>(json_data);
    } catch (error: unknown) {
      const response = handleError('GET', error);
      if (response?.message) {
        return unsafeJsonCast<ResponseType>(response.message);
      }
      throw new Error('GET HTTP Error');
    }
  }

  public get<ResponseType = unknown>(
    route: string,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<ResponseType>> {
    return this._fetch<ResponseType>('GET', route, undefined, opt);
  }

  public async getText(route: string): Promise<string> {
    const finalURL = `${this.callable_url}${route}`;

    const res = await fetch(finalURL, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'text/plain' }
    });

    if (res.status === 423) {
      throw new PasswordProtectedError();
    }

    if (res.status === 429) {
      throw new TooManyRequestsError();
    }

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    return res.text();
  }

  public async getWithForbiddenPossibility<ResponseType = unknown>(
    route: string
  ): Promise<Maybe<ResponseType>> {
    return this._fetchWithForbidden<ResponseType>(route);
  }
  public post<ResponseType = unknown, BodyType = unknown>(
    route: string,
    body: BodyType,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<ResponseType>> {
    return this._fetch('POST', route, body, opt);
  }

  public delete(
    route: string,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<T>> {
    return this._fetch('DELETE', route, undefined, opt);
  }

  public put<ResponseType = unknown, BodyType = unknown>(
    route: string,
    body: BodyType,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<ResponseType>> {
    return this._fetch('PUT', route, body, opt);
  }

  public patch<ResponseType = unknown, BodyType = unknown>(
    route: string,
    body: BodyType,
    opt?: {
      headers?: object;
    }
  ): Promise<Maybe<ResponseType>> {
    return this._fetch('PATCH', route, body, opt);
  }
}

const handleError = (
  method: HTTPMethodeType,
  error: unknown
): { message: string } | void => {
  let errorMessage = 'Unknown error';
  if (error instanceof Error) {
    if (error.message === 'INVALID_PASSWORD' && method === 'GET') {
      return { message: 'password_protected' };
    }
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  // eslint-disable-next-line no-restricted-syntax
  console.error(errorMessage);
};
